import { Component, HostBinding, HostListener, Inject, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { SwPush } from '@angular/service-worker';
import { TranslateComponent } from '@components/translate/translate.component';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@services/alert.service';
import { ConfigLocaleService } from '@services/configLocale.service';
import { GoogleService } from '@services/google.service';
import { LoaderService } from '@services/loader.service';
import { LoggingService } from '@services/logging.service';
import { PushNotificationsService } from '@services/push-notifications.service';
import { PwaInstallService } from '@services/pwa-install.service';
import { TagManagerService } from '@services/tag-manager.service';
import { UpdateService } from '@services/update.service';
import { UserService } from '@services/user.service';
import { Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'gc-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  @HostBinding('style.--vh')
  vh: string;
  @HostBinding('style.--mobiledashboard')
  mobiledashboard: string;
  unsubscribe$ = new Subject<void>();

  showFestive = this.getFromLocalStorage('showFestive') === 'true' ? true : false;
  message: unknown;

  private readonly swPush: SwPush = inject(SwPush);
  private readonly pushNotificationsService: PushNotificationsService = inject(PushNotificationsService);
  private readonly userService: UserService = inject(UserService);
  private readonly googleService: GoogleService = inject(GoogleService);
  constructor(
    private readonly install: PwaInstallService,
    private readonly router: Router,
    private readonly alertService: AlertService,
    private readonly updateService: UpdateService,
    private readonly logger: LoggingService,
    private readonly tagManagerService: TagManagerService,
    private readonly translate: TranslateService,
    private readonly loaderService: LoaderService,
    private readonly configService: ConfigLocaleService,
    @Inject(LOCALE_ID) public locale: string,
  ) {}

  async ngOnInit() {
    this.loaderService.show();
    try {
      this.translateSetup();
    } catch (error) {
      this.alertService.handleErrorDialog$(error, 'Unable to load config files');
      this.loaderService.hide();
    } finally {
      this.loaderService.hide();
    }
    window.dispatchEvent(new Event('resize'));
    this.router.events.pipe(takeUntil(this.unsubscribe$)).subscribe((event) => {
      this.mobiledashboard = this.router.url === '/dashboard' ? '72px' : '0px';
      if (event instanceof NavigationStart) {
        this.logger.addToJourney(event.url);
      } else if (event instanceof NavigationEnd) {
        const gtmTag = {
          event: 'page',
          page: event.url,
        };
        this.tagManagerService.pushTag(gtmTag);
      }
    });

    this.updateService.setupPeriodicUpdateCheck();

    this.userService.profile$
      .pipe(
        take(1),
        tap((profile) => {
          if (profile) {
            this.userService.$userID.set(profile.id);
          }
          if (this.swPush.isEnabled) {
            this.swPushSubscribe();
          } else {
            console.warn('Push notifications are not enabled in this browser/config.');
            this.pushNotificationsService.$swPushCan.set(false);
          }
        }),
      )
      .subscribe();
    await this.googleService.loadMapsLibrary();
  }

  swPushSubscribe() {
    this.swPush.messages.pipe(takeUntil(this.unsubscribe$)).subscribe((payload) => {
      this.message = payload;
    });

    this.swPush.notificationClicks.pipe(takeUntil(this.unsubscribe$)).subscribe(({ notification }) => {
      window.open(notification.data.url, '_blank');
    });
    this.pushNotificationsService.$swPushCan.set(true);
    this.pushNotificationsService.getSubscription$().subscribe();
  }

  @HostListener('window:beforeinstallprompt', ['$event'])
  onBeforeInstallPrompt(event: BeforeInstallPromptEvent) {
    event.preventDefault();
    this.install.deferredPrompt = event;
  }

  @HostListener('window:appinstalled')
  onAppInstalled() {
    this.install.installed = true;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.vh = event.target.innerHeight + 'px';
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event) {
    this.logger.logUserJourney$(event.target.URL, 'beforeunload').subscribe();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
  }

  translateSetup() {
    // The fallback language if a translation isn't found in the current language
    this.translate.setDefaultLang('en-ZA');
    this.translate.setTranslation('en-ZA', require(`../locale/en-ZA.json`));

    const translate = new TranslateComponent(this.translate, this.configService);
    translate.ngOnInit();
  }

  saveToLocalStorage(key: string, value: string) {
    localStorage.setItem(key, value);
  }

  getFromLocalStorage(key: string): string | null {
    return localStorage.getItem(key);
  }

  toggleFestiveIcons() {
    this.showFestive = !this.showFestive;
    this.saveToLocalStorage('showFestive', this.showFestive.toString());
  }

  getIcon() {
    const currentDate = new Date();
    if (currentDate.getMonth() === 11) {
      //Dec Christmas
      return '❅';
    } else if (currentDate.getMonth() === 0 && currentDate.getDate() === 1) {
      //New years
      return '✨';
    } else if (currentDate.getMonth() === 1 && currentDate.getDate() === 14) {
      //Valentines day
      return '❤️';
    }
  }
}
